import { API_ROOT, sendRequest } from '../../supplementary_components/requests';

export const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC', 'PR',
];

export const OOS_OPTIONS = [
    { value: 'NONE', label: '-' },
    { value: 'DRIVER', label: 'OOS Driver' },
    { value: 'EQUIPMENT', label: 'OOS Equipment' },
    { value: 'BOTH', label: 'OOS Both' },
];

export const DATAQ_OPTIONS = [
    { value: 'NOT_STARTED', label: 'Not Started' },
    { value: 'PENDING_SUBMISSION', label: 'Pending Submission' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
    { value: 'ADDITIONAL_INFORMATION_REQUIRED', label: 'Additional Information Required' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'DENIED', label: 'Denied' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'NOT_ELIGIBLE', label: 'Not Eligible' },
];

export const RESULT_OPTIONS = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'CONVICTED_ORIGINAL', label: 'Convicted of the original charge' },
    { value: 'CONVICTED_DIFFERENT', label: 'Convicted of a different charge' },
    { value: 'DISMISSED', label: 'Dismissed or not guilty' },
    { value: 'PAID', label: 'Paid' },
];

const BASIC_ORDER = [
    'Unsafe Driving',
    'HOS Compliance',
    'Driver Fitness',
    'Controlled Substances/Alcohol',
    'Vehicle Maintenance',
    'Hazardous Materials Compliance',
];

export function shortViolationText(item) {
    const description = (item?.officialDescription || item?.description || '').trim();
    if (description && description.length <= 80) return description;
    if (item?.violationGroup) return item.violationGroup;
    if (!description) return '';
    return `${description.slice(0, 77)}…`;
}

export function violationSuggestionLabel(item) {
    const text = shortViolationText(item);
    return text ? `${item.code} · ${text}` : item.code;
}

export function labelFor(options, value) {
    return options.find((option) => option.value === value)?.label || value || '-';
}

export function todayIso() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

export function formatIsoDate(iso) {
    if (!iso) return '-';
    const [year, month, day] = String(iso).split('-');
    if (!year || !month || !day) return iso;
    if (Number(year) === new Date().getFullYear()) return `${month}/${day}`;
    return `${month}/${day}/${year.slice(-2)}`;
}

export function calculatedSeverity(violations) {
    const sums = {};
    (violations || []).forEach((violation) => {
        let weight = Math.max(0, Number(violation.baseWeight) || 0);
        if (violation.outOfService && violation.outOfService !== 'NONE') {
            weight += 2;
        }
        const basic = violation.basic || '';
        sums[basic] = (sums[basic] || 0) + weight;
    });
    const keys = [
        ...BASIC_ORDER.filter((basic) => sums[basic] != null),
        ...Object.keys(sums).filter((basic) => !BASIC_ORDER.includes(basic)),
    ];
    let total = 0;
    keys.forEach((basic) => {
        total += Math.min(30, sums[basic]);
    });
    return total;
}

export function errorMessage(error) {
    if (!error) return 'Something went wrong';
    if (typeof error === 'string') return error;
    return error.customError || error.message || 'Something went wrong';
}

function request(method, path, body, contentType, preSend) {
    return new Promise((resolve, reject) => {
        sendRequest(method, API_ROOT + path, null, resolve, (error) => reject(error), body, contentType, preSend);
    });
}

export const safetyApi = {
    listInspections: (pageNumber = 0) => request('GET', `inspections?pageNumber=${pageNumber}`),
    getInspection: (id) => request('GET', `inspections/${id}`),
    saveInspection: (id, body) => request(id ? 'POST' : 'POST', id ? `inspections/${id}` : 'inspections', body),
    deleteInspection: (id) => request('DELETE', `inspections/${id}`),
    listCitations: (pageNumber = 0) => request('GET', `citations?pageNumber=${pageNumber}`),
    getCitation: (id) => request('GET', `citations/${id}`),
    saveCitation: (id, body) => request('POST', id ? `citations/${id}` : 'citations', body),
    deleteCitation: (id) => request('DELETE', `citations/${id}`),
    suggest: (path) => request('GET', path),
    upload: (path, fields) => {
        const body = new FormData();
        Object.entries(fields).forEach(([key, value]) => body.append(key, value));
        return request('POST', path, body, {}, (form) => form);
    },
    removeInspectionFile: (id, filePath) => request(
        'POST',
        `inspections/removeFile?inspectionId=${id}&filePath=${encodeURIComponent(filePath)}`,
    ),
    removeCitationFile: (id, filePath) => request(
        'POST',
        `citations/removeFile?citationId=${id}&filePath=${encodeURIComponent(filePath)}`,
    ),
};

export async function syncFiles(kind, id, existingFiles, removedPaths, newFiles) {
    const kept = (existingFiles || []).filter((file) => file?.filePath && !(removedPaths || []).includes(file.filePath));
    for (const filePath of removedPaths || []) {
        if (kind === 'inspection') {
            await safetyApi.removeInspectionFile(id, filePath);
        } else {
            await safetyApi.removeCitationFile(id, filePath);
        }
    }
    for (const file of newFiles || []) {
        const fields = { file, fileName: file.name };
        if (kind === 'inspection') fields.inspectionId = id;
        else fields.citationId = id;
        await safetyApi.upload(kind === 'inspection' ? 'inspections/addFile' : 'citations/addFile', fields);
    }
    return kept;
}
